import { useTranslation } from "react-i18next"

import type { HostedAgentSummary } from "@/api/client/hosted-agents"

import { ArrowUpRight } from "@/components/icons"

import { AgentAvatar } from "./agent-avatar"
import { agentIntroduction, agentStarterPrompts } from "../utils/agent-workspace-utils"

type Props = {
  agent: HostedAgentSummary
  onChoose: (text: string) => void
  disabled?: boolean
}

/**
 * Empty state (#3165): the agent's name already stands in the header, so the
 * greeting asks the open question instead of repeating it. One compact
 * promise, then three real, editable tasks — no automatic execution, and no
 * "choose a suggestion" hint line: the cards say that themselves.
 *
 * `name` is still handed to the title so locales that have not dropped the
 * interpolation (de/fi/no fall back to their own copy) keep rendering.
 */
export function AgentGreeting({ agent, onChoose, disabled }: Props) {
  const { t } = useTranslation()
  const introduction = agentIntroduction(agent)
  const configured = agentStarterPrompts(agent)
  const prompts = configured.length
    ? configured
    : [
        t("agents.chat.starter_outline"),
        t("agents.chat.starter_review"),
        t("agents.chat.starter_plan"),
      ]
  return (
    <section
      className="mx-auto flex w-full max-w-3xl flex-col items-center gap-4 px-4 py-6 text-center"
      data-testid="agent-greeting"
    >
      <AgentAvatar agent={agent} className="size-24" />
      <h1 className="font-display text-[28px] leading-[1.05]">
        {t("agents.chat.empty_title", { name: agent.profile.name })}
      </h1>
      {/* The promise is prose: ~52ch keeps it inside DESIGN.md's 30–70ch line. */}
      <p className="text-muted-foreground max-w-[52ch] text-sm leading-relaxed">
        {introduction || t("agents.chat.empty_description")}
      </p>
      <div className="mt-1 grid w-full gap-2.5 sm:grid-cols-3">
        {prompts.map((prompt) => (
          <button
            key={prompt}
            className="card-interactive bg-card focus-visible:ring-ring flex min-h-14 items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm leading-snug focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50"
            disabled={disabled}
            type="button"
            onClick={() => onChoose(prompt)}
          >
            <span className="min-w-0 flex-1">{prompt}</span>
            <ArrowUpRight aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
          </button>
        ))}
      </div>
    </section>
  )
}
